<template>
    <dialogWrap
        v-model="dialogVisible"
        title="批量导入订单"
        :before-close="handleCancel"
    >
        <el-form
            :model="formData"
            :rules="rules"
            label-width="120px"
            ref="formRef"
        >
            <div>
                <div class="tips-area">
                  <p>导入说明：</p>
                  <p v-for="(item, index) in importTips" :key="index">{{ item }}</p>
                </div>
                <div class="form-item-wrapper">
                  <el-form-item prop="rentOrderExcelUrl">
                    <el-upload
                        action=""
                        accept=".xls,.xlsx"
                        :file-list="fileList"
                        :limit="1"
                        :http-request="uploadRequest"
                        :autoUpload="true"
                        :show-file-list="true"
                        :on-remove="handleFileMove"
                    >
                        <el-button
                            type="minor"
                            slot="trigger"
                            :loading="fileUploading"
                            :disabled="fileList.length > 0"
                        >导入数据</el-button>
                        <el-button type="text" size="small">
                            <a :href="templateUrl">下载导入模板</a>
                        </el-button>
                    </el-upload>
                  </el-form-item>
                </div>
            </div>

        </el-form>
        <div class="dialog-footer-box">
            <el-button @click="handleCancel">取消</el-button>
            <el-button type="primary" @click="handleSubmit">提交</el-button>
        </div>
    </dialogWrap>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import axios from 'axios'
import { interfacePrefix } from '@/config'
import { Message } from 'element-ui'
import { batchImportOrder, enterpriseBatchImportOrder } from '@/api/public_import.js'
import { getHead } from '@/utils'
import { useStore, useRouter } from '@/utils/useUtils'


const store = useStore()
const router = useRouter()

const formRef = ref(null)


const dialogVisible = ref(false)

const formData = ref({
  rentOrderExcelUrl: '',
})

const fileList = ref([])
const fileUploading = ref(false)

const rules = {
  rentOrderExcelUrl: [
    {
      required: true,
      message: '请上传文件',
      trigger: 'blur',
    },
  ],
}
// 订单类型
const orderType = ref('')
// 上传文件
const uploadRequest = ({ file }) => {
  const size = file.size / 1024 / 1024
  if (size > 200) {
    Message.error('文件大小不能超过 200MB，建议拆分后再进行操作')
  }
  // eslint-disable-next-line no-useless-escape
  const isExcel = /^(application\/).*((\.sheet)|(\.ms\-excel))$/.test(
    file.type,
  )
  if (!isExcel) {
    fileList.value = []
    Message.error('请上传Excel文件')
  }
  fileUploading.value = true
  const formDataObj = new FormData()
  formDataObj.append('file', file)
  formDataObj.append('group', 'order')
  formDataObj.append('code', 'jaguar_rent_order_import_file')
  axios({
    url: `${interfacePrefix}/fs/standard/upload`,
    method: 'POST',
    data: formDataObj,
    headers: {
      ...getHead(store),
    },
  }).then(({ data: { code, data, msg } }) => {
    if (code === 1) {
      file.url = data
      formData.value.rentOrderExcelUrl = data
      fileList.value.push(file)
      formRef.value.clearValidate('rentOrderExcelUrl')
    } else {
      Message.error(msg)
    }
  }).finally(() => {
    fileUploading.value = false
  })
}

const handleFileMove = () => {
  fileUploading.value = false
  fileList.value = []
}

const handleSubmit = () => {
  if (orderType.value !== 'personal' && orderType.value !== 'enterpriseChild') {
    return
  }
  const method = orderType.value === 'personal' ? batchImportOrder : enterpriseBatchImportOrder
  formRef.value.validate(async (valid) => {
    if (!valid) return
    const { code } = await method({
      rentOrderExcelUrl: formData.value.rentOrderExcelUrl,
      remarks: '',
    })
    if (code === 1) {
      dialogVisible.value = false
      Message.success('导入成功')
      router.push({
        path: '/logCenter/batchImportOrder/list',
        query: {
          type: orderType.value,
        },
      })
    }
  })
}

const handleCancel = () => {
  formData.value.rentOrderExcelUrl = ''
  fileList.value = []
  dialogVisible.value = false
}
const showDialog = (type) => {
  dialogVisible.value = true
  orderType.value = type || 'personal'
}

// 模版地址
const templateUrl = computed(() => {
  // 个人订单导入模版
  if (orderType.value === 'personal') {
    return 'https://server-resource-v4.cheweijia.com/public/assets/templates/jaguar/%E4%B8%AA%E4%BA%BA%E7%A7%9F%E8%B5%81%E8%AE%A2%E5%8D%95%E6%89%B9%E9%87%8F%E5%AF%BC%E5%85%A5%E6%A8%A1%E6%9D%BF.xlsx'
  }
  // 企业租赁子订单模版
  return 'https://server-resource-v4.cheweijia.com/public/assets/templates/jaguar/%E4%BC%81%E4%B8%9A%E7%A7%9F%E8%B5%81%E8%AE%A2%E5%8D%95%E6%89%B9%E9%87%8F%E5%AF%BC%E5%85%A5%E6%A8%A1%E6%9D%BF%20.xlsx'
})

// 导入说明
const importTips = computed(() => {
  const personalTips = [
    '1、订单批量导入前，请确认系统中，车辆和司机都导入完成。',
    '2、订单批量导入前，请确认系统中，门店和管理人员都添加完成。',
    '3、点击“下载导入模版”，并使用模版Excel进行数据填写。',
    '4、确保填写字段信息与模版中标题保持一致。模版中红色字段信息为必填项。',
    '5、模版填写完成后，点击“导入数据”并提交，完成数据导入。',
  ]
  const enterpriseChildTips = [
    '1、订单批量导入前，请确认系统中，已经创建企业主订单。',
    '2、订单批量导入前，请确认系统中，车辆导入完成。',
    '3、点击“下载导入模版”，并使用模版Excel进行数据填写。',
    '4、确保填写字段信息与模版中标题保持一致。模版中红色字段信息为必填项。',
    '5、模版填写完成后，点击“导入数据”并提交，完成数据导入。',
  ]
  // 个人订单导入说明
  if (orderType.value === 'personal') {
    return personalTips
  }
  // 企业租赁子订单导入说明
  return enterpriseChildTips
})

watch(() => dialogVisible.value, (newVal) => {
  if (!newVal) {
    handleCancel()
  }
})


defineExpose({
  showDialog,
})
</script>

<style lang="scss" scoped>
::v-deep .upload {
  margin-left: 0px !important;
}
.dialog-footer-box {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}
.form-item-wrapper {
  margin:20px 0 0 -120px;
}
</style>
